import * as React from "react";
import Autocomplete, {
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import { SxProps, Theme } from "@mui/material/styles";
import { CustomTextField } from "../../formsComponents";
import {
  BASE_INPUT_SX,
  getMultiSelectSx,
  ENR_AUTOCOMPLETE_COMPACT_POPPER,
} from "./EnrAutoComplete.styles";
import { EnrAutoCompleteProps } from "./EnrAutoComplete.types";
import { FIELD_HIGHLIGHT_SX } from "../../../shared";

/**
 * EnrAutoComplete — one generic Autocomplete that owns ALL the shared
 * styling + render behaviour used by the app's filter/search dropdowns, so
 * individual components stay thin (data + fetch logic only).
 *
 * It provides sensible defaults for:
 *   - styling: compact outlined input, compact popup rows, yellow "active
 *     filter" highlight, single-line multi-select chips.
 *   - renderInput:  a CustomTextField with label/error/loading adornment.
 *   - renderTags:   small chips up to `maxVisibleTags`, then a "+N" chip.
 *   - renderOption: label (+ checkbox for multi-select), with italic/muted
 *     styling + divider for "static"/sentinel options (e.g. All / Unassigned).
 *
 * OVERRIDE RULE: any raw MUI render prop the caller passes ALWAYS wins
 * (renderInput / renderTags / renderOption / sx / componentsProps). The
 * defaults only apply when the caller does not provide their own.
 *
 * The "type shift" between components is handled purely through props
 * (getTagLabel / getOptionRowLabel / isStaticOption), never by forking code.
 *
 * Styles live in EnrAutoComplete.styles.ts and types in
 * EnrAutoComplete.types.ts.
 */
export function EnrAutoComplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(props: EnrAutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  const {
    // styling
    isHighlighted = false,
    hasValue = false,
    isFocused = false,
    sx,
    componentsProps,
    // default input
    label,
    required,
    error,
    helperText,
    placeholder,
    loading,
    // default tags
    getTagLabel,
    maxVisibleTags = 1,
    onShowMore,
    // default options
    getOptionRowLabel,
    showCheckbox = false,
    isStaticOption,
    // caller overrides (respected when provided)
    renderInput,
    renderTags,
    renderOption,
    // passthrough
    multiple,
    disabled,
    getOptionLabel,
    ...rest
  } = props;

  /* ----- styling ----- */
  // sx supports an array of entries, including theme callbacks — so the
  // FIELD_HIGHLIGHT_SX factory can be passed directly and resolved by MUI.
  const composedSx = React.useMemo(
    () =>
      [
        BASE_INPUT_SX,
        ...(multiple ? [getMultiSelectSx(hasValue, isFocused)] : []),
        ...(isHighlighted ? [FIELD_HIGHLIGHT_SX] : []),
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ] as SxProps<Theme>,
    [multiple, hasValue, isFocused, isHighlighted, sx],
  );

  /* ----- helpers ----- */
  const labelOf = React.useCallback(
    (option: T): string => {
      if (getOptionRowLabel) return getOptionRowLabel(option);
      if (getOptionLabel) return String(getOptionLabel(option as any));
      return String(option);
    },
    [getOptionRowLabel, getOptionLabel],
  );

  /* ----- default renderInput ----- */
  const defaultRenderInput = React.useCallback(
    (params: AutocompleteRenderInputParams) => (
      <CustomTextField
        {...params}
        label={label}
        required={required}
        placeholder={placeholder}
        // `error` is a message string; convert to the boolean error state and
        // fall back to it for helper text.
        error={!!error}
        helperText={error || helperText}
        value={params.inputProps.value}
        // Keep the floating label shrunk whenever there are selected value(s)
        // (chips) or the field is focused — otherwise the label overlaps the
        // chips on multi-selects.
        InputLabelProps={{
          ...params.InputLabelProps,
          shrink:
            hasValue ||
            isFocused ||
            !!params.inputProps?.value ||
            (params.InputLabelProps as any)?.shrink,
        }}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <React.Fragment>
              {loading ? <CircularProgress color="inherit" size={18} /> : null}
              {params.InputProps.endAdornment}
            </React.Fragment>
          ),
        }}
      />
    ),
    [label, required, placeholder, error, helperText, loading, hasValue, isFocused],
  );

  /* ----- default renderTags ----- */
  const defaultRenderTags = React.useCallback(
    (tagValue: T[], getTagProps: any): React.ReactNode => {
      const chips: React.ReactNode[] = [];
      const visible = tagValue.slice(0, maxVisibleTags);

      visible.forEach((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        chips.push(
          <Chip
            key={key}
            {...tagProps}
            size="small"
            label={getTagLabel ? getTagLabel(option) : labelOf(option)}
            onDelete={disabled ? undefined : tagProps.onDelete}
          />,
        );
      });

      const remaining = tagValue.length - visible.length;
      if (remaining > 0) {
        chips.push(
          <Chip
            key="enr-autocomplete-overflow-chip"
            size="small"
            label={`+${remaining}`}
            onClick={disabled ? undefined : onShowMore}
            sx={{
              cursor: disabled || !onShowMore ? "default" : "pointer",
              margin: "2px 4px 2px 0",
            }}
          />,
        );
      }
      return chips;
    },
    [maxVisibleTags, getTagLabel, labelOf, disabled, onShowMore],
  );

  /* ----- default renderOption ----- */
  const defaultRenderOption = React.useCallback(
    (optionProps: any, option: T, state: { selected: boolean }) => {
      const { key, ...liProps } = optionProps;
      const isStatic = isStaticOption ? isStaticOption(option) : false;
      const text = labelOf(option);
      return (
        <React.Fragment key={key ?? text}>
          <Box
            component="li"
            {...liProps}
            sx={{
              fontSize: "0.8rem",
              ...(isStatic && {
                fontStyle: "italic",
                color: "text.secondary",
              }),
            }}
          >
            {showCheckbox && !isStatic && (
              <Checkbox
                size="small"
                checked={state.selected}
                sx={{ p: 0.5, mr: 0.75 }}
              />
            )}
            {text}
          </Box>
          {isStatic && <Divider sx={{ my: 0.5 }} />}
        </React.Fragment>
      );
    },
    [isStaticOption, labelOf, showCheckbox],
  );

  /* ----- resolve with caller-override priority ----- */
  const effectiveRenderInput = renderInput ?? defaultRenderInput;
  const effectiveRenderTags =
    renderTags ?? (multiple ? (defaultRenderTags as any) : undefined);
  const effectiveRenderOption = renderOption ?? (defaultRenderOption as any);

  return (
    <Autocomplete
      multiple={multiple}
      disabled={disabled}
      loading={loading}
      getOptionLabel={getOptionLabel}
      sx={composedSx}
      componentsProps={
        componentsProps ?? (ENR_AUTOCOMPLETE_COMPACT_POPPER as any)
      }
      renderInput={effectiveRenderInput}
      renderTags={effectiveRenderTags}
      renderOption={effectiveRenderOption}
      {...rest}
    />
  );
}
